import { Link } from '@inertiajs/react'
import { AudioLines, LoaderCircle, Mic, Pencil, Play, SlidersHorizontal, TriangleAlert } from 'lucide-react'
import type { ReactNode } from 'react'
import { CharacterChip } from '@/components/character-chip'
import { ScriptText } from '@/components/script-text'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { adminLines } from '@/routes'
import type { Character, Line } from '@/types'

/** One line of a scene's script, with its audio state and hover actions. */
export function ScriptLine({
  line,
  character,
  coSpeakers,
  productionId,
  playing,
  onPlayFrom,
  onEdit,
  editor,
}: {
  line: Line
  character?: Character
  coSpeakers: Character[]
  productionId: string
  playing: boolean
  onPlayFrom: () => void
  onEdit?: () => void
  /** Shown in place of the text while the line is being edited. */
  editor?: ReactNode
}) {
  const highlight = playing ? 'rounded-r-md bg-primary/10 ring-1 ring-primary/30' : ''
  const edit = onEdit && !editor && (
    <button
      type="button"
      onClick={onEdit}
      className="text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-foreground focus:opacity-100"
      aria-label="Edit line"
      title="Fix the wording or spoken as (E)"
    >
      <Pencil className="size-4" />
    </button>
  )
  if (line.kind === 'direction' || line.kind === 'sound' || !character) {
    return (
      <div id={`line-${line.id}`} className={`group flex scroll-mt-20 items-start gap-2 ${highlight}`}>
        {editor ?? <p className="flex-1 text-sm italic text-muted-foreground">({line.text})</p>}
        {edit}
      </div>
    )
  }

  return (
    <div
      id={`line-${line.id}`}
      className={`group grid scroll-mt-20 gap-0.5 border-l-2 py-0.5 pl-3 transition-colors ${highlight}`}
      style={{ borderColor: character.color }}
    >
      <div className="flex items-center gap-2">
        {[character, ...coSpeakers].map((c) => (
          <CharacterChip key={c.id} character={c} />
        ))}
        <AudioBadge line={line} />
        {line.generating && <LoaderCircle className="size-3.5 animate-spin text-muted-foreground" />}
        {line.spokenAsOutdated && (
          <Badge variant="destructive" className="h-5 px-1.5 text-[10px]">
            spoken as — check
          </Badge>
        )}
        <button
          type="button"
          onClick={onPlayFrom}
          className="ml-auto text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-foreground focus:opacity-100"
          aria-label="Play from here"
          title="Play the scene from this line"
        >
          <Play className="size-4" />
        </button>
        {edit}
        <Link
          href={adminLines.show({ productionId, id: line.id })}
          className="text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-foreground focus:opacity-100"
          aria-label="Line audio"
          title="Line audio: listen, regenerate, spoken as"
        >
          <SlidersHorizontal className="size-4" />
        </Link>
      </div>
      {editor ?? (
        <>
          <p className="whitespace-pre-line text-sm">
            <ScriptText text={line.text} />
          </p>
          {line.spokenAs && (
            <p className="text-xs text-muted-foreground">
              <AudioLines className="mr-1 inline size-3" />
              {line.spokenAs}
            </p>
          )}
        </>
      )}
    </div>
  )
}

function AudioBadge({ line }: { line: Line }) {
  if (line.hasStaleTakes && line.audioSource === 'none') {
    return (
      <Tooltip>
        <TooltipTrigger>
          <TriangleAlert className="size-3.5 text-amber-600" />
        </TooltipTrigger>
        <TooltipContent>Audio was recorded for older wording — regenerate or re-record.</TooltipContent>
      </Tooltip>
    )
  }
  if (line.audioSource === 'recording') {
    return (
      <Badge variant="secondary" className="gap-1">
        <Mic className="size-3" /> cast
      </Badge>
    )
  }
  if (line.audioSource === 'tts' || line.audioSource === 'upload') {
    return (
      <Badge variant="outline" className="gap-1">
        <AudioLines className="size-3" /> {line.audioSource === 'tts' ? 'AI' : 'upload'}
      </Badge>
    )
  }
  return null
}
